// The Board Ask's form and its "Goes to" card (EMC-WEB4, frames 21 and 23): one component for the
// campaign's composer (Fundraisers) and the approvers' page (Board), so the ask a drafter writes is
// the ask an approver changes.
//
// Attachments come from the organization's Collateral through the picker (frame 20a). Each file
// shows under its own Collateral name; the graphics show as one row, "N graphics", with their
// thumbnails; each row has its remove (Jonathan, 2026-10-31).

import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Icon,
  IconButton,
  Textarea,
  testIdProp,
} from '@givmo/design-system';
import { FileText, Images, Paperclip, X } from 'lucide-react';
import { BOARD_ASK_COPY as w } from './board-ask-copy';
import type { BoardAskAttachment, BoardAskRecipient } from './board-ask-wire';
import { CollateralPickerDialog } from './collateral-picker';
import type { CollateralItem } from './org-brand-wire';

export interface BoardAskFormValue {
  message: string;
  attachments: BoardAskAttachment[];
  offersShare: boolean;
  offersPage: boolean;
}

/** `412 KB`, `1.2 MB`: a file's size as the Collateral page would say it; none when unknown. */
export function fileSize(bytes: number): string | null {
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** A Collateral item as the ask attaches it: its own name, its stable address. */
export function asAttachment(item: CollateralItem): BoardAskAttachment {
  return {
    collateralItemId: item.id,
    kind: item.kind === 'image' ? 'image' : 'file',
    title: item.title,
    url: item.url,
    contentType: item.contentType,
    fileName: item.fileName,
    byteSize: 0,
    datedOn: item.datedOn,
  };
}

export interface BoardAskFormProps {
  orgRef: string;
  /** The organization's name, for the picker's line; empty where the page doesn't hold it. */
  orgName: string;
  value: BoardAskFormValue;
  onChange?: (value: BoardAskFormValue) => void;
  /** The campaign's goal as shown ("$60,000"), for "counts toward the $60,000 goal"; none without. */
  goalLabel: string | null;
  editable?: boolean;
  /** Whether this person may add to Collateral (Profile's own write): the picker's "Upload a file". */
  canUpload?: boolean;
  /** Whether the campaign takes fundraiser pages: false turns the choice off and says why. */
  pagesOpen?: boolean;
  /** The organization's date, `YYYY-MM-DD` (`organizationDate`): what the picker hides a dated item by. */
  today: string;
  testId?: string;
}

export function BoardAskForm({
  orgRef,
  orgName,
  value,
  onChange,
  goalLabel,
  editable = true,
  canUpload = false,
  pagesOpen = true,
  today,
  testId = 'board-ask-form',
}: BoardAskFormProps) {
  const [picking, setPicking] = useState(false);
  const set = (patch: Partial<BoardAskFormValue>) => onChange?.({ ...value, ...patch });
  const files = value.attachments.filter((item) => item.kind === 'file');
  const graphics = value.attachments.filter((item) => item.kind === 'image');
  const without = (ids: string[]) =>
    set({ attachments: value.attachments.filter((item) => !ids.includes(item.collateralItemId)) });

  return (
    <Card flush testId={testId}>
      <CardBody className="flex flex-col gap-20">
        {editable ? (
          <Textarea
            label={w.message}
            value={value.message}
            onChange={(event) => set({ message: event.target.value })}
            rows={12}
            maxLength={5000}
            testId={`${testId}-message`}
          />
        ) : (
          <div className="flex flex-col gap-8">
            <span className="font-sans text-s12 font-w600 tracking-givmo text-black">
              {w.message}
            </span>
            <p
              className="whitespace-pre-line font-sans text-s14 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${testId}-message`)}
            >
              {value.message}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-8">
          <span className="font-sans text-s12 font-w600 tracking-givmo text-black">
            {w.attachments}
          </span>
          {value.attachments.length > 0 && (
            <div className="grid gap-10 sm:grid-cols-2">
              {files.map((item) => (
                <AttachmentRow
                  key={item.collateralItemId}
                  icon={FileText}
                  name={item.title}
                  meta={fileSize(item.byteSize) === null ? 'PDF' : w.pdf(fileSize(item.byteSize)!)}
                  onRemove={editable ? () => without([item.collateralItemId]) : undefined}
                  testId={`${testId}-file-${item.collateralItemId}`}
                />
              ))}
              {graphics.length > 0 && (
                <AttachmentRow
                  icon={Images}
                  name={w.graphics(graphics.length)}
                  meta={w.fromCollateral}
                  thumbs={graphics.slice(0, 4)}
                  onRemove={
                    editable
                      ? () => without(graphics.map((item) => item.collateralItemId))
                      : undefined
                  }
                  testId={`${testId}-graphics`}
                />
              )}
            </div>
          )}
          {editable && (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className="self-start"
              leadingIcon={<Icon icon={Paperclip} size={14} aria-hidden />}
              onClick={() => setPicking(true)}
              testId={`${testId}-add-attachments`}
            >
              {w.addAttachments}
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-12 border-t border-border-subtle pt-18">
          <span className="font-sans text-s12 font-w600 tracking-givmo text-black">
            {w.whatDirectorsCanDo}
          </span>
          <div className="grid gap-16 sm:grid-cols-2">
            <Choice
              label={w.shareLabel}
              line={w.shareLine}
              checked={value.offersShare}
              disabled={!editable}
              onChange={(checked) => set({ offersShare: checked })}
              testId={`${testId}-offers-share`}
            />
            <Choice
              label={w.pageLabel}
              line={pagesOpen ? w.pageLine(goalLabel) : w.pagesClosed}
              checked={value.offersPage}
              disabled={!editable || !pagesOpen}
              onChange={(checked) => set({ offersPage: checked })}
              testId={`${testId}-offers-page`}
            />
          </div>
        </div>
      </CardBody>
      {picking && (
        <CollateralPickerDialog
          open={picking}
          onOpenChange={setPicking}
          orgRef={orgRef}
          kinds={['image', 'file']}
          multiple
          canUpload={canUpload}
          today={today}
          selectedIds={value.attachments.map((item) => item.collateralItemId)}
          onChoose={(items) => set({ attachments: items.map(asAttachment) })}
          title={w.pickerTitle}
          body={w.pickerLede(orgName)}
          confirmLabel={w.pickerConfirm}
          testId={`${testId}-picker`}
        />
      )}
    </Card>
  );
}

function AttachmentRow({
  icon,
  name,
  meta,
  thumbs,
  onRemove,
  testId,
}: {
  icon: typeof FileText;
  name: string;
  meta: string;
  thumbs?: BoardAskAttachment[];
  onRemove?: () => void;
  testId: string;
}) {
  return (
    <div
      className={`flex items-center gap-12 rounded-lg border border-border-subtle bg-surface px-12 py-10${thumbs === undefined ? '' : ' sm:col-span-2'}`}
      {...testIdProp(testId)}
    >
      <span className="flex size-[36px] shrink-0 items-center justify-center rounded-md bg-neutral-100 text-icon">
        <Icon icon={icon} size={18} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-sans text-s14 font-w600 text-text-strong">{name}</span>
        <span className="font-sans text-s12 text-text-tertiary">{meta}</span>
      </span>
      {thumbs !== undefined && (
        <span className="flex gap-8">
          {thumbs.map((item) => (
            <img
              key={item.collateralItemId}
              src={item.url}
              alt={item.title}
              title={item.title}
              className="size-[64px] rounded-[6px] object-cover"
            />
          ))}
        </span>
      )}
      {onRemove !== undefined && (
        <IconButton
          aria-label={w.remove(name)}
          tone="secondary"
          size="sm"
          onClick={onRemove}
          testId={`${testId}-remove`}
        >
          <Icon icon={X} size={14} aria-hidden />
        </IconButton>
      )}
    </div>
  );
}

function Choice({
  label,
  line,
  checked,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  line: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
}) {
  return (
    <label className="flex items-start gap-10">
      <Checkbox
        appearance="saas"
        size={18}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next === true)}
        testId={testId}
      />
      <span className="flex flex-col gap-2 pt-[1px]">
        <span className="font-sans text-s14 font-w600 text-text-strong">{label}</span>
        <span className="font-sans text-s13 text-text-secondary">{line}</span>
      </span>
    </label>
  );
}

/** "Goes to all 6 board members": every seat on the board, each with the server's line beside it
 *  (whether the person is a director, and their titles: EMC-BE17). */
export function BoardAskRecipientsCard({
  recipients,
  testId = 'board-ask-recipients',
}: {
  recipients: BoardAskRecipient[];
  testId?: string;
}) {
  return (
    <Card flush testId={testId}>
      <CardHeader title={w.goesTo(recipients.length)} subtitle={w.goesToLine} />
      <CardBody className="pt-12">
        {recipients.length === 0 ? (
          <p className="font-sans text-s14 text-text-secondary">{w.noBoard}</p>
        ) : (
          <ul className="flex flex-col gap-8">
            {recipients.map((person) => (
              <li
                key={person.boardMemberId}
                className="flex items-center justify-between gap-12 font-sans text-s14"
              >
                <span className="text-text-strong">{person.displayName}</span>
                {person.roleLabel !== null && (
                  <span className="text-text-tertiary">{person.roleLabel}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/** The ask as the wire saves it. */
export function draftBody(value: BoardAskFormValue) {
  return {
    message: value.message,
    collateral_item_ids: value.attachments.map((item) => item.collateralItemId),
    offers_share: value.offersShare,
    offers_page: value.offersPage,
  };
}
